import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import translationApi from '../api/translationApi';
import AddTranslationModal, {
   type TranslationFormValues,
} from '../components/translation/AddTranslationModal';
import ImportTranslationsModal, {
   type ImportTranslationsFormValues,
} from '../components/translation/ImportTranslationsModal';
import SuggestAllModal, {
   type SuggestAllFormValues,
} from '../components/translation/SuggestAllModal';
import TranslationCard from '../components/translation/TranslationCard';
import { useSettings } from '../context/SettingsContext';
import type {
   TranslationLanguageKey,
   TranslationObj,
   TranslationProjectKey,
} from '../types';
import { cn } from '../utils/ui';

type Props = {
   project: TranslationProjectKey;
};

const projectNames = {
   boatpark_web: 'Boatpark Web',
   boatpark_app: 'Boatpark App',
   boatpark_api: 'Boatpark API',
};

const get_language_value = (translation: TranslationObj, language: string) => {
   return (translation as unknown as Record<string, string | undefined>)[
      language
   ];
};

const TranslationPage = ({ project }: Props) => {
   const { supportedLanguages } = useSettings();
   const [translations, setTranslations] = useState<TranslationObj[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [showAddTranslationModal, setShowAddTranslationModal] =
      useState(false);
   const [showSuggestAllModal, setShowSuggestAllModal] = useState(false);
   const [showImportTranslationsModal, setShowImportTranslationsModal] =
      useState(false);
   const [showNotTranslated, setShowNotTranslated] = useState(false);
   const [isHeaderElevated, setIsHeaderElevated] = useState(false);
   const [isHeaderPinnedBottom, setIsHeaderPinnedBottom] = useState(false);
   const [headerHeight, setHeaderHeight] = useState(0);
   const [namespaceFilter, setNamespaceFilter] = useState<string | null>(null);
   const [searchQuery, setSearchQuery] = useState('');
   const [namespaceSearchQuery, setNamespaceSearchQuery] = useState('');
   const [isNamespaceDropdownOpen, setIsNamespaceDropdownOpen] =
      useState(false);
   const headerRef = useRef<HTMLDivElement | null>(null);
   const namespaceDropdownRef = useRef<HTMLDivElement | null>(null);

   const toggleNamespaceDropdown = useCallback(() => {
      setIsNamespaceDropdownOpen((prev) => {
         if (prev) {
            setNamespaceSearchQuery('');
         }
         return !prev;
      });
   }, []);

   const namespaceOptions = useMemo(() => {
      const unique = new Set<string>();
      translations.forEach((translation) => {
         if (translation.namespace) {
            unique.add(translation.namespace);
         }
      });
      return Array.from(unique).sort((a, b) => a.localeCompare(b));
   }, [translations]);

   const filteredNamespaceOptions = useMemo(() => {
      const query = namespaceSearchQuery.trim().toLowerCase();
      if (!query) {
         return namespaceOptions;
      }
      return namespaceOptions.filter((option) =>
         option.toLowerCase().includes(query),
      );
   }, [namespaceOptions, namespaceSearchQuery]);

   useEffect(() => {
      if (
         namespaceFilter &&
         !namespaceOptions.includes(namespaceFilter)
      ) {
         setNamespaceFilter(null);
      }
   }, [namespaceFilter, namespaceOptions]);

   useEffect(() => {
      if (!isNamespaceDropdownOpen) {
         return;
      }

      const handleClickOutside = (event: MouseEvent) => {
         if (
            namespaceDropdownRef.current &&
            event.target instanceof Node &&
            !namespaceDropdownRef.current.contains(event.target)
         ) {
            setIsNamespaceDropdownOpen(false);
            setNamespaceSearchQuery('');
         }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
         document.removeEventListener('mousedown', handleClickOutside);
      };
   }, [isNamespaceDropdownOpen]);

   const filteredTranslations = useMemo(() => {
      let result = translations;

      if (showNotTranslated) {
         result = result.filter((t) =>
            supportedLanguages
               .filter((lang) => lang !== 'en')
               .some((lang) => !get_language_value(t, lang)),
         );
      }

      if (namespaceFilter) {
         result = result.filter((t) => t.namespace === namespaceFilter);
      }

      const normalizedSearch = searchQuery.trim().toLowerCase();
      if (normalizedSearch) {
         const matchesSearch = (value: unknown) =>
            typeof value === 'string' &&
            value.toLowerCase().includes(normalizedSearch);

         result = result.filter((t) =>
            matchesSearch(t.namespace) ||
            matchesSearch(t.name) ||
            supportedLanguages.some((lang) =>
               matchesSearch(get_language_value(t, lang)),
            ),
         );
      }

      return result;
   }, [
      namespaceFilter,
      searchQuery,
      showNotTranslated,
      supportedLanguages,
      translations,
   ]);

   const fetchAllTranslations = useCallback(
      async (project: TranslationProjectKey) => {
         try {
            setIsLoading(true);
            const res =
               await translationApi.getAllTranslationsByProject(project);
            console.log('fetchAllTranslations: ', res);
            setTranslations(res);
         } catch (err) {
            console.log('fetchAllTranslations err: ', err);
            setTranslations([]);
         } finally {
            setIsLoading(false);
         }
      },
      [],
   );

   useEffect(() => {
      fetchAllTranslations(project);
   }, [fetchAllTranslations, project]);

   const closeAddTranslationModal = useCallback(() => {
      setShowAddTranslationModal(false);
   }, []);

   const closeSuggestAllModal = useCallback(() => {
      setShowSuggestAllModal(false);
   }, []);

   const closeImportTranslationsModal = useCallback(() => {
      setShowImportTranslationsModal(false);
   }, []);

   useEffect(() => {
      const measure = () => {
         if (headerRef.current) {
            setHeaderHeight(headerRef.current.offsetHeight);
         }
      };

      measure();

      const target = headerRef.current;
      if (typeof ResizeObserver !== 'undefined' && target) {
         const observer = new ResizeObserver(() => measure());
         observer.observe(target);
         return () => {
            observer.disconnect();
         };
      }

      window.addEventListener('resize', measure);
      return () => {
         window.removeEventListener('resize', measure);
      };
   }, []);

   const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
         const currentTop = event.currentTarget.scrollTop;
         const shouldPin = headerHeight > 0 && currentTop > headerHeight;
         setIsHeaderPinnedBottom(shouldPin);
         setIsHeaderElevated(shouldPin || currentTop > 12);
      },
      [headerHeight],
   );

   const onAddTranslation = useCallback(
      async (values: TranslationFormValues) => {
         try {
            const res = await translationApi.addTranslation({
               ...values,
               project,
            });
            console.log('translation res: ', res);
            toast('Added translation successfully', { type: 'success' });
            setTranslations((prev) => [...prev, res]);
            // fetchAllTranslations();
            setShowAddTranslationModal(false);
         } catch (err) {
            console.error('saveTranslation err: ', err);
            toast('Error: ' + err, { type: 'error' });
         }
      },
      [project],
   );

   const onSuggestAll = useCallback(
      async (values: SuggestAllFormValues) => {
         const is_translation_language_key = (
            value: string,
         ): value is TranslationLanguageKey =>
            supportedLanguages.includes(value as TranslationLanguageKey);

         if (!is_translation_language_key(values.language)) {
            toast('Invalid language selected', { type: 'error' });
            return;
         }
         try {
            console.log(values.language);
            const res = await translationApi.translateAll(values.language, project, values.onlyNotTranslated);
            console.log('onSuggestAll res: ', res);
            toast('Suggested all translations successfully', {
               type: 'success',
            });
            setTranslations(res);
            // fetchAllTranslations();
            setShowSuggestAllModal(false);
         } catch (err) {
            console.error('onSuggestAll err: ', err);
            toast('Error: ' + err, { type: 'error' });
         }
      },
      [project, supportedLanguages],
   );

   const onImportTranslations = useCallback(
      async (values: ImportTranslationsFormValues) => {
         try {
            const res = await translationApi.importTranslations(
               project,
               values.csv_content,
            );
            toast(`Imported ${res.length} translations successfully`, {
               type: 'success',
            });
            setTranslations((prev) => {
               const next = [...prev];

               res.forEach((translation) => {
                  const idx = next.findIndex((item) => item.id === translation.id);
                  if (idx > -1) {
                     next[idx] = translation;
                     return;
                  }

                  next.unshift(translation);
               });

               return next;
            });
            setShowImportTranslationsModal(false);
         } catch (err) {
            console.error('onImportTranslations err: ', err);
            toast('Error: ' + err, { type: 'error' });
         }
      },
      [project],
   );

   const onUpdateTranslation = useCallback(
      async (id: TranslationObj['id'], values: TranslationObj) => {
         try {
            const res = await translationApi.updateTranslation({
               id,
               project,
               ...values,
            });
            console.log('onUpdateTranslation res: ', res);
            toast('Updated translation successfully', { type: 'success' });
            setTranslations((prev) => {
               const arr = [...prev];
               const idx = arr.findIndex((t) => t.id === id);
               if (idx > -1) {
                  arr[idx] = res;
               }
               return arr;
            });
            // fetchAllTranslations();
         } catch (err) {
            console.error('onUpdateTranslation err: ', err);
            toast('Error: ' + err, { type: 'error' });
         }
      },
      [project],
   );

   const onDeleteTranslation = useCallback(async (id: TranslationObj['id']) => {
      console.log('onDeleteTranslation()');
      try {
         const res = await translationApi.deleteTranslation(id);
         console.log('onDeleteTranslation res: ', res);
         toast('Deleted translation successfully', { type: 'success' });
         setTranslations((prev) => {
            const arr = [...prev];
            const idx = arr.findIndex((t) => t.id === id);
            if (idx > -1) {
               arr.splice(idx, 1);
            }
            return arr;
         });
         // fetchAllTranslations();
      } catch (err) {
         console.error('onDeleteTranslation err: ', err);
         toast('Error: ' + err, { type: 'error' });
      }
   }, []);

   return (
      <div className='h-full w-full flex-col'>
         {showAddTranslationModal ? (
            <AddTranslationModal
               onClose={closeAddTranslationModal}
               onSave={onAddTranslation}
            />
         ) : null}
         {showSuggestAllModal ? (
            <SuggestAllModal
               onClose={closeSuggestAllModal}
               onSave={onSuggestAll}
            />
         ) : null}
         {showImportTranslationsModal ? (
            <ImportTranslationsModal
               onClose={closeImportTranslationsModal}
               onSave={onImportTranslations}
            />
         ) : null}
         {/* Top View */}
         <div
            className='flex h-full flex-col items-center overflow-auto'
            onScroll={handleScroll}
            style={{ paddingBottom: headerHeight }}
         >
            <div
               ref={headerRef}
               className={cn(
                  'flex w-full justify-center transition-all duration-200',
                  isHeaderPinnedBottom
                     ? 'fixed bottom-0 left-0 right-0 z-10'
                     : 'mt-10',
               )}
            >
               <div
                  className={cn(
                     'flex w-2/3 flex-col gap-y-4 rounded-lg py-4',
                     isHeaderElevated || isHeaderPinnedBottom
                        ? 'bg-white/95 shadow-md backdrop-blur px-6'
                        : 'bg-white/70 backdrop-blur-sm',
                  )}
               >
                  <div className='flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-3'>
                     <span className='text-xl text-boatpark-blue'>{`Project: ${projectNames[project] || 'Unknown Project'} Translations`}</span>
                     <div className='flex flex-row gap-x-4'>
                        <button
                           className='rounded-md border-2 border-boatpark-blue px-4 py-1 text-boatpark-blue duration-200 hover:bg-boatpark-blue hover:text-white'
                           onClick={() => setShowSuggestAllModal(true)}
                        >
                           Suggest All
                        </button>
                        <button
                           className='rounded-md border-2 border-boatpark-blue px-4 py-1 text-boatpark-blue duration-200 hover:bg-boatpark-blue hover:text-white'
                           onClick={() => setShowImportTranslationsModal(true)}
                        >
                           Import
                        </button>
                        <button
                           className='rounded-md border-2 border-boatpark-blue px-4 py-1 text-boatpark-blue duration-200 hover:bg-boatpark-blue hover:text-white'
                           onClick={() => setShowAddTranslationModal(true)}
                        >
                           Add Translation
                        </button>
                     </div>
                  </div>
                  <div className='flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-3'>
                     <div className='flex flex-row items-center'>
                        <button
                           className={cn(
                              'rounded-md border-2 border-boatpark-blue px-4 py-1 text-boatpark-blue',
                              !showNotTranslated
                                 ? 'bg-boatpark-blue font-medium text-white'
                                 : null,
                           )}
                           onClick={() => setShowNotTranslated(false)}
                        >
                           All
                        </button>
                        <button
                           className={cn(
                              'ml-2.5 rounded-md border-2 border-boatpark-blue px-4 py-1 text-boatpark-blue',
                              showNotTranslated
                                 ? 'bg-boatpark-blue font-medium text-white'
                                 : null,
                           )}
                           onClick={() => setShowNotTranslated(true)}
                        >
                           Not Translated
                        </button>
                     </div>
                     <div className='flex flex-row flex-wrap items-center gap-x-3 gap-y-3'>
                        <div
                           ref={namespaceDropdownRef}
                           className='relative'
                        >
                           <button
                              className='flex min-w-[150px] items-center justify-between rounded-md border-2 border-boatpark-blue px-4 py-1 text-left text-boatpark-blue duration-200 hover:bg-boatpark-blue hover:text-white'
                              onClick={toggleNamespaceDropdown}
                              type='button'
                           >
                              <span className='truncate'>
                                 {namespaceFilter ?? 'All namespaces'}
                              </span>
                              <svg
                                 aria-hidden
                                 className='ml-2 h-4 w-4'
                                 fill='none'
                                 stroke='currentColor'
                                 strokeLinecap='round'
                                 strokeLinejoin='round'
                                 strokeWidth={2}
                                 viewBox='0 0 24 24'
                              >
                                 <path d='m6 9 6 6 6-6' />
                              </svg>
                           </button>
                           {isNamespaceDropdownOpen ? (
                              <div
                                 className={cn(
                                    'absolute right-0 z-40 w-60 rounded-md border-2 border-boatpark-blue bg-white shadow-md',
                                    isHeaderPinnedBottom
                                       ? 'bottom-full mb-1'
                                       : 'top-full mt-1',
                                 )}
                              >
                                 <div className='border-b border-boatpark-blue/30 px-3 py-2'>
                                    <input
                                       className='w-full rounded-md border border-boatpark-blue/40 px-3 py-1 text-sm text-boatpark-blue placeholder:text-boatpark-blue/60 focus:border-boatpark-blue focus:outline-none'
                                       onChange={(event) =>
                                          setNamespaceSearchQuery(
                                             event.target.value,
                                          )
                                       }
                                       placeholder='Search'
                                       value={namespaceSearchQuery}
                                    />
                                 </div>
                                 <button
                                    className={cn(
                                       'flex w-full items-center justify-between px-4 py-2 text-left text-sm text-boatpark-blue duration-150 hover:bg-boatpark-blue hover:text-white',
                                       !namespaceFilter
                                          ? 'bg-boatpark-blue text-white'
                                          : null,
                                    )}
                                    onClick={() => {
                                       setNamespaceFilter(null);
                                       setIsNamespaceDropdownOpen(false);
                                       setNamespaceSearchQuery('');
                                    }}
                                    type='button'
                                 >
                                    All namespaces
                                 </button>
                                 <div className='max-h-60 overflow-y-auto'>
                                    {filteredNamespaceOptions.length ? (
                                       filteredNamespaceOptions.map((option) => (
                                          <button
                                             key={option}
                                             className={cn(
                                                'flex w-full items-center justify-between px-4 py-2 text-left text-sm text-boatpark-blue duration-150 hover:bg-boatpark-blue hover:text-white',
                                                namespaceFilter === option
                                                   ? 'bg-boatpark-blue text-white'
                                                   : null,
                                             )}
                                             onClick={() => {
                                                setNamespaceFilter(option);
                                                setIsNamespaceDropdownOpen(false);
                                                setNamespaceSearchQuery('');
                                             }}
                                             type='button'
                                          >
                                             {option}
                                          </button>
                                       ))
                                    ) : (
                                       <div className='px-4 py-3 text-sm text-boatpark-blue/70'>
                                          No namespaces found
                                       </div>
                                    )}
                                 </div>
                              </div>
                           ) : null}
                        </div>
                        <input
                           className='w-full min-w-[180px] rounded-md border-2 border-boatpark-blue px-4 py-1 text-sm leading-[1.8] text-boatpark-blue placeholder:text-boatpark-blue/60 focus:outline-none focus:ring-2 focus:ring-boatpark-blue/40 md:w-64'
                           onChange={(event) => setSearchQuery(event.target.value)}
                           placeholder='Search'
                           type='text'
                           value={searchQuery}
                        />
                     </div>
                  </div>
               </div>
            </div>
            {isHeaderPinnedBottom ? (
               <div
                  aria-hidden
                  className='w-full'
                  style={{ height: headerHeight }}
               />
            ) : null}

            {/* Content View*/}
            <div className='my-10 flex w-2/3 flex-col items-center'>
               {isLoading ? (
                  <b>Loading..</b>
               ) : (
                  <div className='flex w-full flex-col gap-y-4'>
                     {filteredTranslations.map((translationObj) => (
                        <TranslationCard
                           key={translationObj.id}
                           info={translationObj}
                           onDelete={onDeleteTranslation}
                           onUpdate={onUpdateTranslation}
                        />
                     ))}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default TranslationPage;
